import * as React from 'react';
import { Checkbox, Skeleton, TableBody, TableCell, TableRow } from '@mui/material';
import Box from '@mui/material/Box';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useVirtualizer } from '@tanstack/react-virtual';
import { HeadCell } from './EnhancedTableHead';
import { EnhancedTableFilter } from './EnhancedTableFilter';

const ROW_HEIGHT = 53;
const OVERSCAN = 8;
const SKELETON_ROW_COUNT = 12;

export interface VirtualizedTableBodyProps<T extends { id: string; [key: string]: any }> {
  rows: T[];
  headCells: readonly HeadCell<T>[];
  hideCheckbox: boolean;
  handleClick: (ev: React.MouseEvent<unknown>, name: string) => void;
  isSelected: (id: string) => boolean;
  isSelectable?: (row: T) => boolean;
  filters: {
    id: keyof T;
    filterValue: string;
  }[];
  onFilterChanged: (id: keyof T, value: string) => void;
  scrollElementRef: React.RefObject<HTMLDivElement>;
  loading?: boolean;
}

export function VirtualizedTableBody<
  T extends { id: string; [key: string]: any; className?: string }
>({
  headCells,
  rows,
  hideCheckbox,
  handleClick,
  isSelected,
  isSelectable,
  filters,
  onFilterChanged,
  scrollElementRef,
  loading,
}: VirtualizedTableBodyProps<T>) {
  const hasFilter = headCells.some((headerCell) => headerCell.allowFiltering);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0;

  const showSkeleton = loading && rows.length === 0;

  return (
    <TableBody>
      {hasFilter && (
        <EnhancedTableFilter
          columns={headCells}
          filters={filters}
          onFilterChanged={onFilterChanged}
        />
      )}
      {showSkeleton &&
        Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
          <TableRow key={`skeleton-${index}`} style={{ height: ROW_HEIGHT }}>
            {!hideCheckbox && (
              <TableCell padding="checkbox">
                <Skeleton variant="circular" width={24} height={24} />
              </TableCell>
            )}
            {headCells.map((headCell) => (
              <TableCell key={headCell.id as string} align={headCell.align ?? 'left'}>
                <Skeleton variant="text" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      {!showSkeleton && paddingTop > 0 && (
        <TableRow style={{ height: paddingTop }}>
          <TableCell colSpan={headCells.length + (hideCheckbox ? 0 : 1)} sx={{ p: 0, border: 0 }} />
        </TableRow>
      )}
      {!showSkeleton &&
        virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index];
          const isItemSelected = isSelected(row.id);
          const labelId = `enhanced-table-checkbox-${virtualRow.index}`;

          return (
            <TableRow
              hover
              onClick={
                !isSelectable || isSelectable(row)
                  ? (event) => handleClick(event, row.id)
                  : () => {}
              }
              role="checkbox"
              aria-checked={isItemSelected}
              tabIndex={-1}
              key={row.id}
              selected={isItemSelected}
              className={row.className}
              style={{ height: ROW_HEIGHT }}
            >
              {hideCheckbox ? (
                <Box />
              ) : (
                <TableCell padding="checkbox">
                  <Checkbox
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    color="primary"
                    checked={isItemSelected}
                    disabled={isSelectable && !isSelectable(row)}
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </TableCell>
              )}
              {headCells.map((x: HeadCell<T>) => (
                <TableCell align={x.align ?? 'left'} key={x.id as string}>
                  {row[x.id]}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      {!showSkeleton && paddingBottom > 0 && (
        <TableRow style={{ height: paddingBottom }}>
          <TableCell colSpan={headCells.length + (hideCheckbox ? 0 : 1)} sx={{ p: 0, border: 0 }} />
        </TableRow>
      )}
    </TableBody>
  );
}
